import { computed, onMounted, ref, unref } from "vue";
import { getSlotContentByTagNameDeep } from "#ce/utils/slots";

const defaults = ref({});

/**
 * Register defaults for the custom element.
 *
 * @param {Object|vue.ComputedRef<Object>} newDefaults
 */
export function registerDefaults(newDefaults) {
  defaults.value = unref(newDefaults);
}

/**
 * A composable to apply the defaults for the custom element and its child components.
 *
 * @param {import('vue').ComponentObjectPropsOptions} props
 * @param {import('vue').Ref<HTMLElement|null>} root
 * @param {String} componentName
 * @returns {void}
 */
export function useDefaults(props, root, componentName) {
  onMounted(applyDefaultsIfDefined);

  function applyDefaultsIfDefined() {
    if (Object.keys(childComponentDefaults.value).length > 0) {
      applyDefaults();
    }
  }

  function applyDefaults() {
    for (const [component, entry] of Object.entries(childComponentDefaults.value)) {
      applyDefaultsForComponent(component, entry);
    }
  }

  function applyDefaultsForComponent(component, entry) {
    for (const { selector, defaults } of entry) {
      applyDefaultsToElements(component, selector, defaults);
    }
  }

  function applyDefaultsToElements(component, selector, defaults) {
    const elements = findElements(component, selector);

    if (!elements) {
      return;
    }

    for (const element of elements) {
      setElementDefaults(element, defaults);
    }
  }

  /**
   * Set the defaults for the element.
   * It will only set the defaults that are not already set.
   *
   * @param {HTMLElement} element
   * @param {Object} defaults
   */
  function setElementDefaults(element, defaults) {
    // private property to track if the attribute has been set manually.
    element.__undefined_props ??= new Set();

    for (const [key, value] of Object.entries(defaults)) {
      // if the attribute has been set manually - in the dom or in the props, we don't want to override it.
      if (element.__undefined_props.has(key) || element.getAttribute(key) === null) {
        // mark the attribute as set dynamically using a default value.
        element.__undefined_props.add(key);
        element.setAttribute(key, value);
      }
    }
  }

  /**
   * Cache the elements to prevent the same element from being searched for multiple times.
   * Searches are rather expensive, so we want to avoid them if we can.
   *
   * @type {Map<String, Set<HTMLElement>>}
   */
  const elementsCache = new Map();

  /**
   * Find the elements for the component.
   * This will look for the elements in the root element and in the slot content, including nested slots.
   *
   * @param {String} component
   * @param {String} selector
   * @returns {HTMLElement[]}
   */
  function findElements(component, selector) {
    const cachedElements = findElementsFromCache(component);

    if (selector) {
      return cachedElements.filter((element) => element.matches(selector));
    }

    return cachedElements;
  }

  /**
   * Find the elements for the component from the cache.
   * If the elements are not in the cache, it will find them from the root and the slot content.
   * It will then cache the elements and return them.
   *
   * @param {String} component
   * @returns {HTMLElement[]}
   */
  function findElementsFromCache(component) {
    const cachedElements = elementsCache.get(component);

    if (cachedElements) {
      return cachedElements;
    }

    const elements = findElementsFromRoot(component);
    elementsCache.set(component, elements);

    return elements;
  }

  /**
   * Find the elements for the component from the root and the slot content.
   * This is a relatively expensive operation, so we want to avoid it if we can.
   *
   * @param {String} component
   * @returns {HTMLElement[]}
   */
  function findElementsFromRoot(component) {
    const rootElements = Array.from(root.value?.querySelectorAll(component));
    const slotElements = getSlotContentByTagNameDeep(root.value, component);

    return [...rootElements, ...slotElements];
  }

  const componentDefaults = computed(() => defaults.value?.[componentName]);

  /**
   * Regex to match the component and the selector.
   *
   * @example
   * "SpButton[ref='button']" -> { component: "SpButton", selector: "[ref='button']" }
   * "SpButton" -> { component: "SpButton", selector: undefined }
   */
  const componentDefaultsRegex = /(?<component>Sp[^[]*)(?<selector>\[.*\])?/;

  /**
   * Get the child component defaults, grouped by the component name.
   * Each group is sorted by the selector.
   * First come the defaults for the component, then the defaults for the elements with the selector.
   *
   * @returns {import("vue").ComputedRef<Object<string, {selector: String, defaults: Object}[]>>}
   */
  const childComponentDefaults = computed(() => computeChildComponentDefaults());

  /**
   * Compute the child component defaults, grouped by the component name.
   * Match candidates must match the SP Component name, and can optionally match the selector.
   * Unmatched defaults are ignored.
   *
   * @returns {Object<string, {selector: String, defaults: Object}[]>}
   */
  function computeChildComponentDefaults() {
    return Object.entries(componentDefaults.value ?? {})
      .map(([key, defaults]) => ({ key, defaults, match: key.match(componentDefaultsRegex) }))
      .filter(({ match }) => match)
      .reduce((entries, { match, defaults }) => {
        const { component, selector } = match.groups;
        const entry = { selector, defaults };

        entries[component] ??= [];

        // Process component defaults first, then selector-specific defaults
        selector ? entries[component].push(entry) : entries[component].unshift(entry);

        return entries;
      }, {});
  }
}
